
import {
  File, Image, Option, RichTextElement,
} from 'fsxa-api'
import { FSXABaseSection } from 'fsxa-pattern-library'
import { Component } from 'nuxt-property-decorator'
import TextImage from '../../TextImage.vue'
import ITextImage from '../../../shared/general/interfaces/ITextImage'
import { mapImage } from '../../../shared/general/services/ImageService'
import { buildPlayerElements, getVideoFormat } from '../../../shared/general/services/VideoUrlService'

type MediaType = 'image' | 'video' | 'triangles'
type Alignment = 'left' | 'right' | 'centered'

interface IPayload {
  st_alt_text ?: string
  st_headline ?: string
  st_image ?: Image
  st_image_alignement ?: Option
  st_image_caption ?: string
  st_image_decorative ?: boolean
  st_intro_text ?: string
  st_mp4 ?: File
  st_ogv ?: File
  st_webm ?: File
  st_text ?: RichTextElement[]
  st_title_text ?: string
  st_toggle_gap ?: boolean
  st_video_alignement ?: Option
  st_video_caption ?: string
  st_video_description ?: string
  st_video_format ?: Option
  st_video_id ?: string
  st_video_link ?: string
  st_vimeo_privacy_hash ?: string
  st_video_preview_image ?: Image
  st_zoom_image ?: Image
  st_zoom_image_active ?: boolean
  st_zoom_image_alt_text ?: string
  st_type ?: Option
  st_show_design_shield ?: boolean
  st_triangles_alignment ?: Option
}

@Component({
  name: 'StTextImage',
  components: {
    TextImage,
  },
})
export default class StTextImage extends FSXABaseSection<IPayload> {
  private get alignment () : Alignment {
    const getAlignment = (value ?: Option) : Alignment => {
      if (!value || !value.key) return 'centered'
      switch (value.key) {
        case 'left':
        case 'right':
        case 'centered': return value.key
        default: return 'centered'
      }
    }
    switch (this.mediaType) {
      case 'image': return getAlignment(this.payload.st_image_alignement)
      case 'video': return getAlignment(this.payload.st_video_alignement)
      case 'triangles': return getAlignment(this.payload.st_triangles_alignment)
      default: return getAlignment(undefined)
    }
  }

  private get toggleGap () : boolean {
    return !!this.payload.st_toggle_gap
  }

  private get anchorName () : string | undefined {
    return this.$store.getters['AnchorLinks/getAnchorNameForSection'](this.id)
  }

  private get caption () : string | undefined {
    return this.mediaType === 'image' ? this.payload.st_image_caption : this.payload.st_video_caption
  }

  private get headline () : string {
    return this.payload.st_headline || ''
  }

  private get introText () : string {
    return this.payload.st_intro_text || ''
  }

  private get displayDekraShield () : boolean {
    return (this.payload.st_show_design_shield ?? false) && !this.payload.st_image_caption?.length
  }

  private get content () : RichTextElement[] {
    return this.payload.st_text || []
  }

  private get mediaType () : MediaType {
    return (this.payload.st_type?.key as MediaType | undefined) || 'image'
  }

  private get image () : Image | undefined {
    if (this.mediaType === 'triangles') return undefined
    if (this.mediaType === 'video') return this.payload.st_video_preview_image
    return this.payload.st_image
  }

  private get media () : ITextImage | null {
    if (!this.image) return null

    const lightboxImage = mapImage(this.payload.st_zoom_image, this.payload.st_zoom_image_alt_text, this.payload.st_zoom_image_alt_text)
    return {
      image: mapImage(this.image, this.payload.st_alt_text, this.payload.st_title_text, this.payload.st_image_decorative),
      caption: this.caption,
      title: this.payload.st_title_text || '',
      lightboxImage: lightboxImage.resolutions.length ? lightboxImage : undefined,
      video: {
        id: this.payload.st_video_id || '',
        link: this.payload.st_video_link || '',
        title: this.payload.st_video_caption,
        format: getVideoFormat(this.payload.st_video_format?.key),
        htmlPlayerElements: buildPlayerElements({
          mp4: this.payload.st_mp4,
          webm: this.payload.st_webm,
          ogv: this.payload.st_ogv,
        }),
        privacyHash: this.payload.st_vimeo_privacy_hash,
      },
      lightbox: !!this.payload.st_zoom_image_active,
      mediaType: this.mediaType,
    }
  }
}
